/**
 * Default sub-heads seeded for each fund. These are pragmatic placeholders;
 * the CO will edit them via Settings → Sub-Heads once the system is live.
 *
 * `type` indicates whether the sub-head accepts CREDIT entries (INCOME),
 * DEBIT entries (EXPENSE), or both.
 */

import { FundCode } from './funds.js';

export const SubHeadType = {
  INCOME: 'INCOME',
  EXPENSE: 'EXPENSE',
  BOTH: 'BOTH',
} as const;

export type SubHeadType = (typeof SubHeadType)[keyof typeof SubHeadType];

export interface DefaultSubHead {
  fundCode: FundCode;
  code: string;
  name: string;
  type: SubHeadType;
}

export const DEFAULT_SUB_HEADS: readonly DefaultSubHead[] = [
  // Private Fund
  { fundCode: FundCode.PVT, code: 'PVT_RECEIPT', name: 'Receipt', type: SubHeadType.INCOME },
  { fundCode: FundCode.PVT, code: 'PVT_WELFARE', name: 'Welfare', type: SubHeadType.EXPENSE },
  {
    fundCode: FundCode.PVT,
    code: 'PVT_HOSPITALITY',
    name: 'Hospitality',
    type: SubHeadType.EXPENSE,
  },
  {
    fundCode: FundCode.PVT,
    code: 'PVT_MISC',
    name: 'Misc Discretionary',
    type: SubHeadType.EXPENSE,
  },

  // Public Fund
  { fundCode: FundCode.PUB, code: 'PUB_GRANT', name: 'Grant Received', type: SubHeadType.INCOME },
  { fundCode: FundCode.PUB, code: 'PUB_STATIONERY', name: 'Stationery', type: SubHeadType.EXPENSE },
  { fundCode: FundCode.PUB, code: 'PUB_POL', name: 'POL', type: SubHeadType.EXPENSE },
  { fundCode: FundCode.PUB, code: 'PUB_RATION', name: 'Ration', type: SubHeadType.EXPENSE },
  {
    fundCode: FundCode.PUB,
    code: 'PUB_REPAIR',
    name: 'Repair & Maintenance',
    type: SubHeadType.EXPENSE,
  },
  { fundCode: FundCode.PUB, code: 'PUB_TRANSPORT', name: 'Transport', type: SubHeadType.EXPENSE },

  // Regimental Fund
  {
    fundCode: FundCode.REG,
    code: 'REG_SUBSCRIPTION',
    name: 'Subscriptions',
    type: SubHeadType.INCOME,
  },
  { fundCode: FundCode.REG, code: 'REG_SPORTS', name: 'Sports', type: SubHeadType.EXPENSE },
  { fundCode: FundCode.REG, code: 'REG_RECREATION', name: 'Recreation', type: SubHeadType.EXPENSE },
  {
    fundCode: FundCode.REG,
    code: 'REG_FUNCTIONS',
    name: 'Functions & Events',
    type: SubHeadType.EXPENSE,
  },
  { fundCode: FundCode.REG, code: 'REG_WELFARE', name: 'Welfare', type: SubHeadType.EXPENSE },

  // Imprest Fund
  { fundCode: FundCode.IMP, code: 'IMP_RECEIPT', name: 'Cash Receipt', type: SubHeadType.INCOME },
  { fundCode: FundCode.IMP, code: 'IMP_PETTY', name: 'Petty Cash', type: SubHeadType.EXPENSE },
  {
    fundCode: FundCode.IMP,
    code: 'IMP_REFRESHMENT',
    name: 'Refreshments',
    type: SubHeadType.EXPENSE,
  },
  {
    fundCode: FundCode.IMP,
    code: 'IMP_OFFICE',
    name: 'Office Supplies',
    type: SubHeadType.EXPENSE,
  },

  // Field Mess
  {
    fundCode: FundCode.FM,
    code: 'FM_SUBSCRIPTION',
    name: 'Mess Subscriptions',
    type: SubHeadType.INCOME,
  },
  { fundCode: FundCode.FM, code: 'FM_PROVISIONS', name: 'Provisions', type: SubHeadType.EXPENSE },
  { fundCode: FundCode.FM, code: 'FM_FUEL', name: 'Cooking Fuel', type: SubHeadType.EXPENSE },
  { fundCode: FundCode.FM, code: 'FM_STAFF', name: 'Mess Staff Salary', type: SubHeadType.EXPENSE },
  {
    fundCode: FundCode.FM,
    code: 'FM_CROCKERY',
    name: 'Crockery & Utensils',
    type: SubHeadType.EXPENSE,
  },

  // Canteen
  { fundCode: FundCode.CAN, code: 'CAN_SALES', name: 'Sales Receipts', type: SubHeadType.INCOME },
  {
    fundCode: FundCode.CAN,
    code: 'CAN_INVENTORY',
    name: 'Inventory Purchase',
    type: SubHeadType.EXPENSE,
  },
  {
    fundCode: FundCode.CAN,
    code: 'CAN_STAFF',
    name: 'Canteen Staff Salary',
    type: SubHeadType.EXPENSE,
  },
  { fundCode: FundCode.CAN, code: 'CAN_UTILITIES', name: 'Utilities', type: SubHeadType.EXPENSE },
  {
    fundCode: FundCode.CAN,
    code: 'CAN_MAINTENANCE',
    name: 'Maintenance',
    type: SubHeadType.EXPENSE,
  },

  // Helmet Fund
  { fundCode: FundCode.HF, code: 'HF_DONATION', name: 'Donations', type: SubHeadType.INCOME },
  { fundCode: FundCode.HF, code: 'HF_WELFARE', name: 'Welfare', type: SubHeadType.EXPENSE },
  {
    fundCode: FundCode.HF,
    code: 'HF_EDUCATION',
    name: 'Education Support',
    type: SubHeadType.EXPENSE,
  },
  { fundCode: FundCode.HF, code: 'HF_MEDICAL', name: 'Medical Aid', type: SubHeadType.EXPENSE },
];
